import { Box, Typography, Button } from '@mui/material';
import * as FaIcons from 'react-icons/fa';
import React, { useState, useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

import { useTranslation } from '../context/TranslationContext';
import { updateBackend } from '../actions/backendVersionActions';
import {
  SET_SAVED_TARGET_VERSION,
  SET_UPDATE_REQUESTED,
} from '../context/backendStatus/backendStatusActions';
import { useBackendStatusContext } from '../context/backendStatus/backendStatusContext';
import { ScreenResolutionContext } from '../context/screenResolutionContext';
import { usePollingManager } from '../context/PollingManagerContext';
import { TestWorkflowAction } from '../constants/testWorkflowState';
import { useAppWorkflowContext } from '../context/AppWorkflowContext';
import { useAppConfig } from '../context/AppConfigContext.jsx';

import ConfirmationDialog from './ConfirmationModals/ConfirmationDialog';

const UpdateButton = ({ forceShow = false, selectedVersion }) => {
  const backendStatus = useBackendStatusContext();
  const theme = useTheme();
  const { translate } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { dispatchWorkflow } = useAppWorkflowContext();
  const { runningState } = usePollingManager();
  const isTestNotFailedAndNotReported = runningState?.isTestNotFailedAndNotReported ?? true;
  const { api } = useAppConfig();
  const acquireBaseUrl = api.acquire.baseUrl;

  const { isCompactWidth, isMediumWidth, isExpandedWidth } = useContext(ScreenResolutionContext);
  const isMediumScreen = isExpandedWidth;
  const isSmallScreen = isCompactWidth || isMediumWidth;

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleUpdate = async () => {
    setIsDialogOpen(false);
    //avoiding re-entrancy when the button is clicked multiple times or when the backend is already updating
    if (backendStatus.state.isUpdating) {
      return;
    }
    backendStatus.dispatch({ type: SET_UPDATE_REQUESTED });
    dispatchWorkflow({ type: TestWorkflowAction.ENTER_BACKEND_UPDATING });

    // Use targetVersion from state if selectedVersion is null
    const versionToUpdate = selectedVersion || backendStatus.state.targetVersion;
    backendStatus.dispatch({ type: SET_SAVED_TARGET_VERSION });
    await updateBackend(versionToUpdate, acquireBaseUrl);
  };
  return (
    (forceShow || backendStatus.state.isUpdatePossible) && (
      <Box
        sx={{
          mr: 1,
          display: 'flex',
          alignItems: 'center',
          color: theme.palette.primary.main,
          flexWrap: 'nowrap',
          gap: isSmallScreen ? '4px' : '8px',
        }}
      >
        <Typography sx={{ fontSize: isSmallScreen ? '12px' : isMediumScreen ? '14px' : '16px' }}>
          {translate('settings.updateAvailable')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FaIcons.FaDownload style={{ fontSize: isSmallScreen ? '12px' : '16px' }} />}
          onClick={handleOpenDialog}
          loading={backendStatus.state.isUpdateRequested}
          size={'small'}
          sx={{
            ml: 1,
            fontSize: isSmallScreen ? '10px' : isMediumScreen ? '11px' : '13px',
            whiteSpace: isSmallScreen ? 'wrap' : 'nowrap',
            display: 'flex',
            alignItems: 'center',
            pl: '12px',
            pr: '12px',
            minWidth: isSmallScreen ? '100px' : 'auto',
            maxWidth: '100%',
          }}
        >
          {translate('settings.install')} {backendStatus.state.targetVersion}
        </Button>
        <ConfirmationDialog
          title={
            isTestNotFailedAndNotReported
              ? translate('confirmUpdate.confirm')
              : translate('confirmUpdate.updateNotPossible')
          }
          subtitle={
            isTestNotFailedAndNotReported
              ? translate('confirmUpdate.areYouSureUpdate')
              : translate('header.waitUntilTestAnalysisComplete')
          }
          isOpen={isDialogOpen}
          confirmDisabled={!isTestNotFailedAndNotReported}
          icon={
            isTestNotFailedAndNotReported ? (
              <QuestionMarkIcon style={{ fontSize: '74px' }} />
            ) : (
              <PriorityHighIcon style={{ fontSize: '74px' }} />
            )
          }
          onClose={handleClose}
          onConfirm={handleUpdate}
        />
      </Box>
    )
  );
};

export default UpdateButton;
